import React, { useState, useEffect } from 'react';
import RealisticBusinessDirectory from './RealisticBusinessDirectory';
import AdminBusinessPanel from './AdminBusinessPanel';
import { getSystemAssets } from '../utils/systemAssets';
import JoinLiveClassButton from './LiveStream/JoinLiveClassButton';

function BlinkitStyleDashboard({ user, onLogout }) {
  const [currentView, setCurrentView] = useState('home');
  const [language, setLanguage] = useState('english');
  const [systemAssets, setSystemAssets] = useState(getSystemAssets());
  const [profilePhoto, setProfilePhoto] = useState(systemAssets.parentImage);
  const [showProfilePreview, setShowProfilePreview] = useState(false);

  // Update system assets when they change
  useEffect(() => {
    const assets = getSystemAssets();
    setSystemAssets(assets);
    if (!profilePhoto || profilePhoto === systemAssets.parentImage) {
      setProfilePhoto(assets.parentImage);
    }
  }, []);

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size (60KB = 60 * 1024 bytes)
      if (file.size > 60 * 1024) {
        alert(language === 'english' ? 'Photo must be less than 60KB' : 'फोटो 60KB से कम होनी चाहिए');
        return;
      }
      
      // Check file type
      const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
      if (!allowedTypes.includes(file.type)) {
        alert(language === 'english' ? 'Only PNG, JPG, JPEG files allowed' : 'केवल PNG, JPG, JPEG फाइलें अनुमतित हैं');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePhoto(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const translations = {
    english: {
      welcome: 'Welcome back',
      parentOf: 'Parent of',
      class: 'Class',
      businesses: 'Businesses',
      jobOpenings: 'Job Openings',
      newConnections: 'New Connections',
      communityPosts: 'Community Posts',
      exploreCommunity: 'Explore Community',
      businessDirectory: 'Business Directory',
      businessDesc: 'Discover trusted parent-owned businesses in your community',
      verifiedBusinesses: 'verified businesses',
      jobOpportunities: 'Job Opportunities',
      jobDesc: 'Find flexible work options from fellow parents',
      activePositions: 'active positions',
      communityPostsTitle: 'Community Posts',
      communityDesc: 'Share experiences and connect with other parents',
      recentPosts: 'recent posts',
      myProfile: 'My Profile',
      profileDesc: 'Manage your business profile and connections',
      complete: 'complete',
      recentActivity: 'Recent Activity',
      joinedCommunity: 'joined the community',
      postedJob: 'posted a part-time teaching job',
      sharedPost: 'shared a community post',
      hoursAgo: 'hours ago',
      dayAgo: 'day ago',
      home: 'Home',
      business: 'Business',
      jobs: 'Jobs',
      community: 'Community'
    },
    hindi: {
      welcome: 'वापसी पर स्वागत है',
      parentOf: 'के अभिभावक',
      class: 'कक्षा',
      businesses: 'व्यवसाय',
      jobOpenings: 'नौकरी के अवसर',
      newConnections: 'नए कनेक्शन',
      communityPosts: 'समुदाय पोस्ट',
      exploreCommunity: 'समुदाय देखें',
      businessDirectory: 'व्यवसाय निर्देशिका',
      businessDesc: 'अपने समुदाय में विश्वसनीय अभिभावक-स्वामित्व व्यवसाय खोजें',
      verifiedBusinesses: 'सत्यापित व्यवसाय',
      jobOpportunities: 'नौकरी के अवसर',
      jobDesc: 'साथी अभिभावकों से लचीले काम के विकल्प खोजें',
      activePositions: 'सक्रिय पद',
      communityPostsTitle: 'समुदाय पोस्ट',
      communityDesc: 'अनुभव साझा करें और अन्य अभिभावकों से जुड़ें',
      recentPosts: 'हाल की पोस्ट',
      myProfile: 'मेरी प्रोफ़ाइल',
      profileDesc: 'अपनी व्यावसायिक प्रोफ़ाइल और कनेक्शन प्रबंधित करें',
      complete: 'पूर्ण',
      recentActivity: 'हाल की गतिविधि',
      joinedCommunity: 'समुदाय में शामिल हुए',
      postedJob: 'अंशकालिक शिक्षण नौकरी पोस्ट की',
      sharedPost: 'समुदाय पोस्ट साझा की',
      hoursAgo: 'घंटे पहले',
      dayAgo: 'दिन पहले',
      home: 'होम',
      business: 'व्यवसाय',
      jobs: 'नौकरियां',
      community: 'समुदाय'
    }
  };

  const t = translations[language];

  if (currentView === 'business') {
    return (
      <RealisticBusinessDirectory 
        user={user} 
        onBack={() => setCurrentView('home')}
      />
    );
  }

  // Check if user is Admin Level 2 (Business Manager)
  if (user.name === 'BUSINESS' || user.parentId === 'ADMIN2_BUSINESS') {
    return (
      <AdminBusinessPanel 
        user={user} 
        onLogout={onLogout}
        language={language}
        setLanguage={setLanguage}
      />
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F8F9FA',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      color: '#2D3748'
    }}>
      <JoinLiveClassButton />
      {/* Header */}
      <div style={{
        background: 'white',
        padding: '12px 16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              backgroundImage: `url(${systemAssets.risingLogo})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              borderRadius: '8px'
            }}></div>
            <div>
              <h1 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#2D3748',
                margin: 0
              }}>Rising 360</h1>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Language Toggle */}
            <div style={{
              display: 'flex',
              background: '#F7FAFC',
              borderRadius: '6px',
              padding: '2px'
            }}>
              <button
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  background: language === 'english' ? 'white' : 'transparent',
                  color: language === 'english' ? '#2D3748' : '#718096',
                  fontWeight: language === 'english' ? '500' : '400'
                }}
                onClick={() => setLanguage('english')}
              >
                EN
              </button>
              <button
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  background: language === 'hindi' ? 'white' : 'transparent',
                  color: language === 'hindi' ? '#2D3748' : '#718096',
                  fontWeight: language === 'hindi' ? '500' : '400'
                }}
                onClick={() => setLanguage('hindi')}
              >
                हिं
              </button>
            </div>
            
            <button 
              style={{
                background: '#F7FAFC',
                color: '#4A5568',
                border: '1px solid #E2E8F0',
                padding: '6px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
              onClick={onLogout}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '16px'
      }}>
        {/* Profile Section */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #E2E8F0'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '16px'
          }}>
            {/* Profile Photo */}
            <div style={{
              position: 'relative',
              width: '80px',
              height: '80px'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: profilePhoto ? `url(${profilePhoto})` : '#4299E1',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px',
                fontWeight: '700',
                color: 'white',
                border: '3px solid #E2E8F0'
              }}>
                {!profilePhoto && user.name.charAt(0).toUpperCase()}
              </div>
              <input
                type="file"
                accept=".png,.jpg,.jpeg"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
                id="photo-upload"
              />
              <label
                htmlFor="photo-upload"
                style={{
                  position: 'absolute',
                  bottom: '0',
                  right: '0',
                  width: '24px',
                  height: '24px',
                  background: '#10B981',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: 'white',
                  border: '2px solid white'
                }}
              >
                📷
              </label>
            </div>
            
            {/* Profile Info */}
            <div style={{ flex: 1 }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#2D3748',
                margin: '0 0 4px 0'
              }}>{t.welcome}, {user.name}!</h2>
              <p style={{
                fontSize: '14px',
                color: '#718096',
                margin: '0 0 8px 0'
              }}>{user.name} - {t.parentOf} {user.studentName} • {t.class} {user.studentClass}</p>
              
              {/* Profile Details */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '12px'
              }}>
                <span style={{
                  background: '#E6FFFA',
                  color: '#065F46',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '500'
                }}>📞 1234567890</span>
                <span style={{
                  background: '#FFF5F5',
                  color: '#991B1B',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '500'
                }}>📧 {user.name.toLowerCase().replace(' ', '.')}@email.com</span>
                <span style={{
                  background: '#FFFBEB',
                  color: '#92400E',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '500'
                }}>🏠 Sector 15, Gurgaon</span>
              </div>
              
              {/* Profile Stats */}
              <div style={{
                display: 'flex',
                gap: '16px'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#10B981'
                  }}>4.8</div>
                  <div style={{
                    fontSize: '10px',
                    color: '#6B7280'
                  }}>{language === 'english' ? 'Rating' : 'रेटिंग'}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#3B82F6'
                  }}>23</div>
                  <div style={{
                    fontSize: '10px',
                    color: '#6B7280'
                  }}>{language === 'english' ? 'Reviews' : 'समीक्षा'}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#8B5CF6'
                  }}>156</div>
                  <div style={{
                    fontSize: '10px',
                    color: '#6B7280'
                  }}>{language === 'english' ? 'Connections' : 'कनेक्शन'}</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Profile Actions */}
          <div style={{
            display: 'flex',
            gap: '12px',
            paddingTop: '16px',
            borderTop: '1px solid #F7FAFC'
          }}>
            <button 
              style={{
                background: '#3B82F6',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                flex: 1
              }}
              onClick={() => alert('Edit Profile - Coming Soon!')}
            >
              📝 {language === 'english' ? 'Edit Profile' : 'प्रोफाइल संपादित करें'}
            </button>
            <button 
              style={{
                background: '#10B981',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                flex: 1
              }}
              onClick={() => setShowProfilePreview(true)}
            >
              👁️ {language === 'english' ? 'Preview Profile' : 'प्रोफाइल पूर्वावलोकन'}
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#2D3748',
            margin: '0 0 16px 0'
          }}>{language === 'english' ? 'Quick Actions' : 'त्वरित कार्य'}</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '12px'
          }}>
            <button 
              style={{
                background: '#E6FFFA',
                border: '1px solid #81E6D9',
                padding: '12px',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'center'
              }}
              onClick={() => setCurrentView('business')}
            >
              <div style={{ fontSize: '20px', marginBottom: '4px' }}>🏪</div>
              <div style={{ fontSize: '12px', color: '#2D3748', fontWeight: '500' }}>
                {language === 'english' ? 'Browse Shops' : 'दुकानें देखें'}
              </div>
            </button>
            <button style={{
              background: '#FFF5F5',
              border: '1px solid #FEB2B2',
              padding: '12px',
              borderRadius: '8px',
              cursor: 'pointer',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '20px', marginBottom: '4px' }}>🛒</div>
              <div style={{ fontSize: '12px', color: '#2D3748', fontWeight: '500' }}>
                {language === 'english' ? 'My Cart' : 'मेरी कार्ट'}
              </div>
            </button>
            <button style={{
              background: '#FFFAF0',
              border: '1px solid #F6E05E',
              padding: '12px',
              borderRadius: '8px',
              cursor: 'pointer',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '20px', marginBottom: '4px' }}>📦</div>
              <div style={{ fontSize: '12px', color: '#2D3748', fontWeight: '500' }}>
                {language === 'english' ? 'My Orders' : 'मेरे ऑर्डर'}
              </div>
            </button>
            <button style={{
              background: '#F0FFF4',
              border: '1px solid #9AE6B4',
              padding: '12px',
              borderRadius: '8px',
              cursor: 'pointer',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '20px', marginBottom: '4px' }}>⭐</div>
              <div style={{ fontSize: '12px', color: '#2D3748', fontWeight: '500' }}>
                {language === 'english' ? 'Favorites' : 'पसंदीदा'}
              </div>
            </button>
          </div>
        </div>

        {/* Community Overview */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#2D3748',
              margin: 0
            }}>{language === 'english' ? 'Parent Community Overview' : 'अभिभावक समुदाय अवलोकन'}</h3>
            <span style={{
              fontSize: '12px',
              color: '#10B981',
              background: '#ECFDF5',
              padding: '4px 8px',
              borderRadius: '12px',
              fontWeight: '500'
            }}>{language === 'english' ? 'Active Now' : 'अभी सक्रिय'}</span>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '12px'
          }}>
            <div 
              style={{
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: 'white',
                padding: '16px',
                borderRadius: '8px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                gridColumn: '1 / -1'
              }}
              onClick={() => setCurrentView('business')}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <div style={{ fontSize: '28px', marginBottom: '6px' }}>🏪</div>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                marginBottom: '4px'
              }}>247</div>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '2px'
              }}>{t.businessDirectory}</div>
              <div style={{
                fontSize: '11px',
                opacity: '0.9'
              }}>{language === 'english' ? 'Tap to explore trusted businesses' : 'भरोसेमंद व्यवसाय देखने के लिए टैप करें'}</div>
            </div>
            
            <div style={{
              background: '#FFFBEB',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center',
              border: '1px solid #FDE68A'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '4px' }}>💼</div>
              <div style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#D97706',
                marginBottom: '2px'
              }}>63</div>
              <div style={{
                fontSize: '12px',
                color: '#92400E'
              }}>{t.jobOpenings}</div>
              <div style={{
                fontSize: '10px',
                color: '#6B7280',
                marginTop: '4px'
              }}>{language === 'english' ? 'Flexible opportunities' : 'लचीले अवसर'}</div>
            </div>
            
            <div style={{
              background: '#F3E8FF',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center',
              border: '1px solid #C4B5FD'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '4px' }}>🤝</div>
              <div style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#7C3AED',
                marginBottom: '2px'
              }}>156</div>
              <div style={{
                fontSize: '12px',
                color: '#5B21B6'
              }}>{language === 'english' ? 'Connections' : 'कनेक्शन'}</div>
              <div style={{
                fontSize: '10px',
                color: '#6B7280',
                marginTop: '4px'
              }}>{language === 'english' ? 'Growing network' : 'बढ़ता नेटवर्क'}</div>
            </div>
            
            <div style={{
              background: '#FEF2F2',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center',
              border: '1px solid #FECACA'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '4px' }}>💬</div>
              <div style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#DC2626',
                marginBottom: '2px'
              }}>89</div>
              <div style={{
                fontSize: '12px',
                color: '#991B1B'
              }}>{t.communityPosts}</div>
              <div style={{
                fontSize: '10px',
                color: '#6B7280',
                marginTop: '4px'
              }}>{language === 'english' ? 'Active discussions' : 'सक्रिय चर्चाएं'}</div>
            </div>
          </div>
        </div>

        {/* Main Services - What Parents Need Most */}
        <div style={{
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#2D3748',
              margin: 0
            }}>{language === 'english' ? 'Essential Daily Services' : 'दैनिक आवश्यक सेवाएं'}</h3>
            <span style={{
              fontSize: '12px',
              color: '#6B7280'
            }}>{language === 'english' ? 'Tap to explore' : 'देखने के लिए टैप करें'}</span>
          </div>
          
          <div style={{
            display: 'grid',
            gap: '12px'
          }}>
            {/* Business Directory - Most Important */}
            <div 
              style={{
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: 'white',
                padding: '20px',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
              onClick={() => setCurrentView('business')}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 25px rgba(16, 185, 129, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <div style={{
                position: 'absolute',
                top: '-10px',
                right: '-10px',
                width: '60px',
                height: '60px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '50%'
              }}></div>
              <div style={{ position: 'relative', zIndex: 2 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>🛒</div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      margin: '0 0 4px 0'
                    }}>{language === 'english' ? 'Grocery & Food Delivery' : 'किराना और खाना डिलीवरी'}</h4>
                    <p style={{
                      fontSize: '13px',
                      opacity: '0.9',
                      margin: 0,
                      lineHeight: '1.4'
                    }}>{language === 'english' ? 'Fresh groceries, home-cooked meals, snacks delivered to your door' : 'ताजा किराना, घर का खाना, स्नैक्स आपके दरवाजे तक'}</p>
                  </div>
                  <div style={{
                    fontSize: '20px',
                    opacity: '0.8'
                  }}>→</div>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  <span style={{
                    fontSize: '11px',
                    background: 'rgba(255,255,255,0.2)',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontWeight: '500'
                  }}>45+ {language === 'english' ? 'Stores' : 'दुकानें'}</span>
                  <span style={{
                    fontSize: '11px',
                    background: 'rgba(255,255,255,0.2)',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontWeight: '500'
                  }}>⚡ {language === 'english' ? '30min Delivery' : '30मिनट डिलीवरी'}</span>
                </div>
              </div>
            </div>

            {/* Job Opportunities */}
            <div 
              style={{
                background: 'white',
                padding: '16px',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                border: '1px solid #FDE68A'
              }}
              onClick={() => alert('Job Opportunities - Coming Soon!')}
              onMouseEnter={(e) => {
                e.target.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.15)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                background: '#FFFBEB',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>💼</div>
              <div style={{ flex: 1 }}>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#2D3748',
                  margin: '0 0 2px 0'
                }}>{t.jobOpportunities}</h4>
                <p style={{
                  fontSize: '12px',
                  color: '#718096',
                  margin: '0 0 4px 0'
                }}>{language === 'english' ? 'Part-time work, home-based jobs, flexible hours for parents' : 'अंशकालिक काम, घर से काम, लचीले समय'}</p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span style={{
                    fontSize: '10px',
                    color: '#D97706',
                    background: '#FEF3C7',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    fontWeight: '500'
                  }}>63 {language === 'english' ? 'Open' : 'खुले'}</span>
                  <span style={{
                    fontSize: '10px',
                    color: '#059669',
                    background: '#D1FAE5',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    fontWeight: '500'
                  }}>{language === 'english' ? 'Remote OK' : 'रिमोट ठीक'}</span>
                </div>
              </div>
              <div style={{
                fontSize: '16px',
                color: '#F59E0B'
              }}>→</div>
            </div>

            {/* Community & Help */}
            <div 
              style={{
                background: 'white',
                padding: '16px',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                border: '1px solid #FECACA'
              }}
              onClick={() => alert('Community Posts - Coming Soon!')}
              onMouseEnter={(e) => {
                e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.15)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                background: '#FEF2F2',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>💬</div>
              <div style={{ flex: 1 }}>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#2D3748',
                  margin: '0 0 2px 0'
                }}>{language === 'english' ? 'Community & Help' : 'समुदाय और सहायता'}</h4>
                <p style={{
                  fontSize: '12px',
                  color: '#718096',
                  margin: '0 0 4px 0'
                }}>{language === 'english' ? 'Ask questions, share tips, get parenting advice from community' : 'सवाल पूछें, सुझाव साझा करें, परामर्श लें'}</p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span style={{
                    fontSize: '10px',
                    color: '#DC2626',
                    background: '#FEE2E2',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    fontWeight: '500'
                  }}>89 {language === 'english' ? 'Posts' : 'पोस्ट'}</span>
                  <span style={{
                    fontSize: '10px',
                    color: '#7C3AED',
                    background: '#EDE9FE',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    fontWeight: '500'
                  }}>{language === 'english' ? '24/7 Active' : '24/7 सक्रिय'}</span>
                </div>
              </div>
              <div style={{
                fontSize: '16px',
                color: '#EF4444'
              }}>→</div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '80px'
        }}>
          <div style={{
            padding: '16px 16px 12px 16px',
            borderBottom: '1px solid #F7FAFC'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#2D3748',
              margin: 0
            }}>{t.recentActivity}</h3>
          </div>
          
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              borderBottom: '1px solid #F7FAFC'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: '#4299E1',
                color: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '600'
              }}>SM</div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '13px',
                  color: '#2D3748',
                  marginBottom: '2px'
                }}>
                  <strong>Sarah's Tailoring</strong> {t.joinedCommunity}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: '#718096'
                }}>2 {t.hoursAgo}</div>
              </div>
              <div style={{
                width: '24px',
                height: '24px',
                background: '#F0FDF4',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px'
              }}>🏪</div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              borderBottom: '1px solid #F7FAFC'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: '#48BB78',
                color: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '600'
              }}>RK</div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '13px',
                  color: '#2D3748',
                  marginBottom: '2px'
                }}>
                  <strong>Raj Kumar</strong> {t.postedJob}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: '#718096'
                }}>5 {t.hoursAgo}</div>
              </div>
              <div style={{
                width: '24px',
                height: '24px',
                background: '#FFFBEB',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px'
              }}>💼</div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: '#ED8936',
                color: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '600'
              }}>AP</div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '13px',
                  color: '#2D3748',
                  marginBottom: '2px'
                }}>
                  <strong>Anita Patel</strong> {t.sharedPost}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: '#718096'
                }}>1 {t.dayAgo}</div>
              </div>
              <div style={{
                width: '24px',
                height: '24px',
                background: '#FEF2F2',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px'
              }}>💬</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'white',
        borderTop: '1px solid #E2E8F0',
        padding: '8px 0',
        display: 'flex',
        justifyContent: 'space-around',
        boxShadow: '0 -1px 3px rgba(0,0,0,0.1)'
      }}>
        <button style={{
          background: 'none',
          border: 'none',
          padding: '8px 12px',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px',
          borderRadius: '6px',
          minWidth: '60px',
          color: '#4299E1'
        }}>
          <div style={{ fontSize: '18px' }}>🏠</div>
          <div style={{
            fontSize: '10px',
            fontWeight: '500',
            color: '#4299E1'
          }}>{t.home}</div>
        </button>
        <button 
          style={{
            background: 'none',
            border: 'none',
            padding: '8px 12px',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            borderRadius: '6px',
            minWidth: '60px'
          }}
          onClick={() => setCurrentView('business')}
        >
          <div style={{ fontSize: '18px' }}>🏪</div>
          <div style={{
            fontSize: '10px',
            fontWeight: '500',
            color: '#718096'
          }}>{t.business}</div>
        </button>
        <button style={{
          background: 'none',
          border: 'none',
          padding: '8px 12px',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px',
          borderRadius: '6px',
          minWidth: '60px'
        }}>
          <div style={{ fontSize: '18px' }}>💼</div>
          <div style={{
            fontSize: '10px',
            fontWeight: '500',
            color: '#718096'
          }}>{t.jobs}</div>
        </button>
        <button style={{
          background: 'none',
          border: 'none',
          padding: '8px 12px',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px',
          borderRadius: '6px',
          minWidth: '60px'
        }}>
          <div style={{ fontSize: '18px' }}>💬</div>
          <div style={{
            fontSize: '10px',
            fontWeight: '500',
            color: '#718096'
          }}>{t.community}</div>
        </button>
      </div>

      {/* Profile Preview Modal */}
      {showProfilePreview && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto',
            position: 'relative'
          }}>
            <button
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: '#6B7280'
              }}
              onClick={() => setShowProfilePreview(false)}
            >
              ×
            </button>

            <div style={{
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '100px',
                height: '100px',
                background: `url(${profilePhoto})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: '50%',
                margin: '0 auto 16px',
                border: '3px solid #E2E8F0'
              }}></div>
              
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#2D3748',
                margin: '0 0 4px 0'
              }}>{user.name}</h2>
              
              <p style={{
                fontSize: '14px',
                color: '#718096',
                margin: '0 0 12px 0'
              }}>{t.parentOf} {user.studentName} • {t.class} {user.studentClass}</p>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                marginBottom: '16px'
              }}>
                <span style={{ color: '#F59E0B' }}>⭐⭐⭐⭐⭐</span>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#2D3748'
                }}>4.8</span>
                <span style={{
                  fontSize: '12px',
                  color: '#6B7280'
                }}>(23 reviews)</span>
              </div>
            </div>

            <div style={{
              background: '#F8FAFC',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#2D3748',
                margin: '0 0 12px 0'
              }}>{language === 'english' ? 'Contact Information' : 'संपर्क जानकारी'}</h3>
              
              <div style={{ marginBottom: '8px' }}>
                <span style={{
                  fontSize: '12px',
                  color: '#6B7280'
                }}>{language === 'english' ? 'Phone:' : 'फोन:'}</span>
                <span style={{
                  fontSize: '14px',
                  color: '#2D3748',
                  marginLeft: '8px',
                  fontWeight: '500'
                }}>1234567890</span>
              </div>
              
              <div>
                <span style={{
                  fontSize: '12px',
                  color: '#6B7280'
                }}>{language === 'english' ? 'Email:' : 'ईमेल:'}</span>
                <span style={{
                  fontSize: '14px',
                  color: '#2D3748',
                  marginLeft: '8px',
                  fontWeight: '500'
                }}>{user.name.toLowerCase().replace(' ', '.')}@email.com</span>
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                background: '#F0FDF4',
                padding: '12px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#059669'
                }}>156</div>
                <div style={{
                  fontSize: '11px',
                  color: '#065F46'
                }}>{language === 'english' ? 'Connections' : 'कनेक्शन'}</div>
              </div>
              
              <div style={{
                background: '#EFF6FF',
                padding: '12px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#2563EB'
                }}>23</div>
                <div style={{
                  fontSize: '11px',
                  color: '#1E40AF'
                }}>{language === 'english' ? 'Reviews' : 'समीक्षा'}</div>
              </div>
              
              <div style={{
                background: '#FEF3C7',
                padding: '12px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#D97706'
                }}>Jan 2023</div>
                <div style={{
                  fontSize: '11px',
                  color: '#92400E'
                }}>{language === 'english' ? 'Member Since' : 'सदस्य से'}</div>
              </div>
            </div>

            <div style={{
              background: '#ECFDF5',
              border: '1px solid #BBF7D0',
              padding: '12px',
              borderRadius: '8px',
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              <span style={{
                fontSize: '14px',
                color: '#059669',
                fontWeight: '600'
              }}>
                🏆 {language === 'english' ? 'Verified Parent Community Member' : 'सत्यापित अभिभावक समुदाय सदस्य'}
              </span>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button style={{
                flex: 1,
                background: '#10B981',
                color: 'white',
                border: 'none',
                padding: '10px 16px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}>
                📞 {language === 'english' ? 'Call' : 'कॉल करें'}
              </button>
              <button style={{
                flex: 1,
                background: '#3B82F6',
                color: 'white',
                border: 'none',
                padding: '10px 16px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}>
                💬 {language === 'english' ? 'Message' : 'संदेश'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BlinkitStyleDashboard;